import { useEffect } from "react";
import { PORTS, COUNTRIES_SERVED, SERVICES, PRIORITY_COUNTRIES, PROCESS_STEPS } from "@/data/content";
import { GLOSSARY } from "@/data/geo";

/**
 * Emits richer JSON-LD (BreadcrumbList, ItemList of services & ports,
 * AggregateRating, and Organization knownAs aliases) so that AI answer
 * engines and Google can crawl deep semantics of the business.
 */
export default function SchemaGraph() {
  useEffect(() => {
    const id = "ccp-graph-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://customclearancepartner.in/" },
            { "@type": "ListItem", position: 2, name: "Services", item: "https://customclearancepartner.in/#services" },
            { "@type": "ListItem", position: 3, name: "Ports & Airports", item: "https://customclearancepartner.in/#ports" },
            { "@type": "ListItem", position: 4, name: "Countries", item: "https://customclearancepartner.in/#countries" },
            { "@type": "ListItem", position: 5, name: "Contact", item: "https://customclearancepartner.in/#contact" },
          ],
        },
        {
          "@type": "ItemList",
          name: "Customs Clearance Services in India",
          itemListElement: SERVICES.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name: s.name,
              description: s.long,
              areaServed: { "@type": "Country", name: "India" },
              provider: { "@id": "https://customclearancepartner.in/#org" },
            },
          })),
        },
        {
          "@type": "ItemList",
          name: "Airports & Ports Covered by Custom Clearance Partner",
          itemListElement: PORTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Place",
              name: `${p.city} — ${p.type}`,
              description: p.ops,
              containedInPlace: { "@type": "Country", name: "India" },
            },
          })),
        },
        {
          "@type": "ItemList",
          name: "Countries of Freight Forwarders We Serve",
          itemListElement: COUNTRIES_SERVED.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: { "@type": "Country", name: c.name },
          })),
        },
        {
          "@type": "Organization",
          "@id": "https://customclearancepartner.in/#org-aliases",
          alternateName: [
            "CCP India",
            "Custom Clearance Partner India",
            "customclearancepartner.in",
            "customclearancepartner.com",
            "India Customs House Agent",
            "India CHA for Freight Forwarders",
          ],
        },
        {
          "@type": "ItemList",
          name: "Priority country lanes for freight forwarders shipping into India",
          itemListElement: PRIORITY_COUNTRIES.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name: `Customs clearance in India for freight forwarders from ${c.name}`,
              serviceType: "Customs Clearance and Last-Mile Delivery",
              areaServed: [
                { "@type": "Country", name: c.name },
                { "@type": "Country", name: "India" },
              ],
              description: c.intent,
              provider: { "@id": "https://customclearancepartner.in/#org" },
            },
          })),
        },
        {
          "@type": "HowTo",
          name: "How to clear an import shipment into India via Custom Clearance Partner",
          description:
            "A four-step workflow that Custom Clearance Partner uses to clear air-express and commercial cargo into India for international freight forwarders.",
          totalTime: "PT6H",
          step: PROCESS_STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: s.body,
          })),
        },
        {
          "@type": "DefinedTermSet",
          name: "India Customs Clearance Glossary",
          hasDefinedTerm: GLOSSARY.map((g) => ({
            "@type": "DefinedTerm",
            name: g.term,
            description: g.def,
            inDefinedTermSet: "https://customclearancepartner.in/#glossary",
          })),
        },
        {
          "@type": "AboutPage",
          "@id": "https://customclearancepartner.in/#about",
          about: { "@id": "https://customclearancepartner.in/#org" },
          mainEntity: { "@id": "https://customclearancepartner.in/#business" },
          speakable: {
            "@type": "SpeakableSpecification",
            xpath: [
              "/html/head/title",
              "/html/head/meta[@name='description']/@content",
              "//*[@id='hero-heading']",
              "//*[@id='quick-facts']",
            ],
          },
        },
        {
          "@type": "AggregateRating",
          itemReviewed: { "@id": "https://customclearancepartner.in/#business" },
          ratingValue: "4.9",
          reviewCount: "127",
          bestRating: "5",
          worstRating: "1",
        },
      ],
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, []);
  return null;
}
